import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { GripVertical, Plus, Trash2, Pencil, Lock } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KanbanColuna } from '../../services/kanbanService';

interface KanbanColumnManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  colunas: KanbanColuna[];
  onCriar: (data: { titulo: string; cor: string }) => Promise<void>;
  onAtualizar: (id: string, data: { titulo?: string; cor?: string }) => Promise<void>;
  onExcluir: (coluna: KanbanColuna) => void;
  onReordenar: (colunas: Array<{ id: string; ordem: number }>) => Promise<void>;
}

const COLOR_OPTIONS = [
  { label: 'Azul', value: '#3B82F6' },
  { label: 'Verde', value: '#22C55E' },
  { label: 'Laranja', value: '#F97316' },
  { label: 'Vermelho', value: '#EF4444' },
  { label: 'Roxo', value: '#8B5CF6' },
  { label: 'Cinza', value: '#6B7280' },
  { label: 'Rosa', value: '#EC4899' },
  { label: 'Amarelo', value: '#EAB308' },
];

const SortableColumnItem = ({
  coluna,
  onEdit,
  onDelete,
}: {
  coluna: KanbanColuna;
  onEdit: (c: KanbanColuna) => void;
  onDelete: (c: KanbanColuna) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: coluna.id });
  const isFixa = coluna.tipo === 'fixa';
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-600 border border-gray-200 dark:border-slate-500 group"
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-500 flex-shrink-0"
      >
        <GripVertical size={14} />
      </button>
      <span
        className="w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: coluna.cor || '#6B7280' }}
      />
      <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-200">
        {coluna.titulo}
      </span>
      {isFixa && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-500 text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <Lock size={10} />
          Fixa
        </span>
      )}
      {!isFixa && (
        <>
          <button
            onClick={() => onEdit(coluna)}
            className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors opacity-0 group-hover:opacity-100"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={() => onDelete(coluna)}
            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
          >
            <Trash2 size={14} />
          </button>
        </>
      )}
    </div>
  );
};

export const KanbanColumnManagerModal = ({
  open,
  onOpenChange,
  colunas,
  onCriar,
  onAtualizar,
  onExcluir,
  onReordenar
}: KanbanColumnManagerModalProps) => {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [titulo, setTitulo] = useState('');
  const [cor, setCor] = useState('#3B82F6');
  const [saving, setSaving] = useState(false);
  const [localColunas, setLocalColunas] = useState<KanbanColuna[]>([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    setLocalColunas([...colunas].sort((a, b) => a.ordem - b.ordem));
    setHasOrderChanged(false);
  }, [colunas]);

  useEffect(() => {
    if (!open) {
      setShowForm(false);
      setEditId(null);
      setTitulo('');
      setCor('#3B82F6');
      setHasOrderChanged(false);
    }
  }, [open]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localColunas.findIndex(c => c.id === active.id);
    const newIndex = localColunas.findIndex(c => c.id === over.id);
    const newOrder = arrayMove(localColunas, oldIndex, newIndex);
    setLocalColunas(newOrder);
    setHasOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      await onReordenar(localColunas.map((c, i) => ({ id: c.id, ordem: i })));
      setHasOrderChanged(false);
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!titulo.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await onAtualizar(editId, { titulo: titulo.trim(), cor });
      } else {
        await onCriar({ titulo: titulo.trim(), cor });
      }
      setShowForm(false);
      setEditId(null);
      setTitulo('');
      setCor('#3B82F6');
    } catch (e) {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (coluna: KanbanColuna) => {
    setEditId(coluna.id);
    setTitulo(coluna.titulo);
    setCor(coluna.cor || '#6B7280');
    setShowForm(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] dark:bg-slate-700 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="dark:text-white">Gerenciar Colunas do Kanban</DialogTitle>
          <DialogDescription className="dark:text-gray-300">
            Arraste para reordenar. Colunas fixas não podem ser excluídas.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={localColunas.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {localColunas.map(coluna => (
                  <SortableColumnItem
                    key={coluna.id}
                    coluna={coluna}
                    onEdit={handleEdit}
                    onDelete={(c) => onExcluir(c)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {showForm && (
            <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-4 space-y-3 bg-purple-50/50 dark:bg-purple-900/10">
              <Label className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
                {editId ? 'Editar Coluna' : 'Nova Coluna'}
              </Label>
              <div className="grid gap-2">
                <Label className="text-sm text-gray-600 dark:text-gray-300">Nome</Label>
                <Input
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder="Ex: Em Análise"
                  className="dark:bg-slate-600 dark:text-white dark:border-slate-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-sm text-gray-600 dark:text-gray-300">Cor</Label>
                <div className="flex gap-1.5 flex-wrap">
                  {COLOR_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setCor(opt.value)}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        cor === opt.value
                          ? 'border-gray-900 dark:border-white scale-110 shadow-md'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: opt.value }}
                      title={opt.label}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowForm(false); setEditId(null); setTitulo(''); }}
                  className="dark:bg-slate-600 dark:text-white dark:border-slate-500"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!titulo.trim() || saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {saving ? 'Salvando...' : editId ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-gray-200 dark:border-slate-600 pt-3">
          {hasOrderChanged && (
            <Button
              size="sm"
              onClick={handleSaveOrder}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white mr-auto"
            >
              {saving ? 'Salvando...' : 'Salvar Ordem'}
            </Button>
          )}
          {!showForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(true)}
              className="dark:bg-slate-600 dark:text-white dark:border-slate-500 border-dashed"
            >
              <Plus size={14} className="mr-1" />
              Adicionar Coluna
            </Button>
          )}
          <DialogClose asChild>
            <Button variant="secondary" size="sm" className="dark:bg-slate-600 dark:text-white">
              Fechar
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
